import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { shallowEqual, useSelector } from "react-redux";
import { Prompt } from "react-router-dom";
import { LinearProgress, Paper, Snackbar } from "@material-ui/core";
import {
  Card,
  CardBody,
  CardHeader,
  CardHeaderToolbar,
} from "../../../_metronic/_partials/controls";
import { useSubheader } from "../../../_metronic/layout";
import { SnackbarWrapper } from "../Snackbar/SnackbarWrapper";
import { FeaturedCourseSlots } from "./Components/FeaturedCourseSlots";
import { AvailableCoursesTable } from "./Components/AvailableCoursesTable";

// Server surface (routes/branch-course.js): GET returns every regular offering
// of the branch with its featured slot and whether the public website currently
// shows it; PUT replaces the whole ordered list in one call.
const FEATURED_URL = "api/branch-course/featured/";

// Fallback only — the server sends max_featured on every read.
const DEFAULT_MAX_FEATURED = 4;

const EMPTY_LIST = [];

// Ties the "all slots taken" explanation to the Feature buttons it disables.
const AT_CAP_MESSAGE_ID = "featured-courses-at-cap";

const sameOrder = (a, b) =>
  a.length === b.length && a.every((id, i) => id === b[i]);

// Move the item at `index` by `delta` slots, returning a new array. Out-of-range
// moves are no-ops so the caller does not have to guard the ends.
const swap = (ids, index, delta) => {
  const target = index + delta;
  if (target < 0 || target >= ids.length) return ids;
  const next = ids.slice();
  next[index] = ids[target];
  next[target] = ids[index];
  return next;
};

export default function FeaturedCoursesPage() {
  const subheader = useSubheader();
  const setSubheaderTitle = subheader.setTitle;
  useEffect(() => {
    setSubheaderTitle("Featured Courses");
  }, [setSubheaderTitle]);

  const branchId = useSelector((state) => {
    const b = state.auth.branches && state.auth.branches.currentBranch;
    // -1 is the app-wide "no branch selected" sentinel; normalize it to null.
    return b && b.branch_id !== -1 ? b.branch_id : null;
  }, shallowEqual);

  // Attach the token explicitly. The global axios interceptor is off and
  // axios.defaults only gets x-auth-token once getUserByToken() runs during
  // rehydrate, so a hard reload would otherwise race that saga into a 401.
  const authToken = useSelector((state) => state.auth.authToken, shallowEqual);

  const [courses, setCourses] = useState(EMPTY_LIST);
  const [maxFeatured, setMaxFeatured] = useState(DEFAULT_MAX_FEATURED);
  const [selectedIds, setSelectedIds] = useState(EMPTY_LIST);
  // What the branch is actually publishing right now — the baseline for the
  // dirty check and for Discard.
  const [savedIds, setSavedIds] = useState(EMPTY_LIST);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [toastOpen, setToastOpen] = useState(false);
  const [toastVariant, setToastVariant] = useState("success");
  const [toastMessage, setToastMessage] = useState("");

  // The branch the screen is currently showing. Every response is checked
  // against it before it is allowed to write state: switching branches while a
  // read is in flight would otherwise let the slow reply for the branch the
  // manager just left overwrite the one they are now looking at, and the same
  // applies to the reload that follows a save.
  const shownBranchRef = useRef(null);

  const fetchFeatured = useCallback(
    async (targetBranchId) => {
      if (!targetBranchId || !authToken) return;
      const isCurrent = () => shownBranchRef.current === targetBranchId;
      setLoading(true);
      setError("");
      try {
        const res = await axios.get(FEATURED_URL + targetBranchId, {
          headers: { "x-auth-token": authToken },
        });
        if (!isCurrent()) return;
        const payload = (res.data && res.data.data) || {};
        const rows = payload.courses || [];
        const pinned = rows
          .filter((c) => c.featured_order != null)
          .sort((a, b) => a.featured_order - b.featured_order)
          .map((c) => c.branch_course_id);
        setCourses(rows);
        setMaxFeatured(payload.max_featured || DEFAULT_MAX_FEATURED);
        setSelectedIds(pinned);
        setSavedIds(pinned);
      } catch (e) {
        if (!isCurrent()) return;
        setError(
          e.response && e.response.status === 403
            ? "You don't have access to the featured courses of this branch."
            : "Could not load this branch's streams."
        );
        setCourses(EMPTY_LIST);
        setSelectedIds(EMPTY_LIST);
        setSavedIds(EMPTY_LIST);
      } finally {
        if (isCurrent()) setLoading(false);
      }
    },
    [authToken]
  );

  useEffect(() => {
    shownBranchRef.current = branchId;
    fetchFeatured(branchId);
  }, [branchId, fetchFeatured]);

  const courseById = useMemo(() => {
    const map = new Map();
    courses.forEach((c) => map.set(c.branch_course_id, c));
    return map;
  }, [courses]);

  // Selection order is the slot order, so read the strip straight off it. A
  // stale id (the offering was deleted elsewhere) simply drops out.
  const featured = useMemo(
    () => selectedIds.map((id) => courseById.get(id)).filter(Boolean),
    [selectedIds, courseById]
  );

  const available = useMemo(() => {
    const picked = new Set(selectedIds);
    const needle = search.trim().toLowerCase();
    const result = [];
    for (const course of courses) {
      if (picked.has(course.branch_course_id)) continue;
      if (
        needle &&
        !`${course.course_name} ${course.course_code}`
          .toLowerCase()
          .includes(needle)
      ) {
        continue;
      }
      result.push(course);
    }
    return result;
  }, [courses, selectedIds, search]);

  const isDirty = !sameOrder(selectedIds, savedIds);
  const atCap = selectedIds.length >= maxFeatured;
  const busy = loading || saving;

  const handleAdd = useCallback(
    (id) =>
      setSelectedIds((ids) =>
        ids.length >= maxFeatured || ids.includes(id) ? ids : [...ids, id]
      ),
    [maxFeatured]
  );

  const handleRemove = useCallback(
    (id) => setSelectedIds((ids) => ids.filter((existing) => existing !== id)),
    []
  );

  const handleMoveUp = useCallback(
    (index) => setSelectedIds((ids) => swap(ids, index, -1)),
    []
  );

  const handleMoveDown = useCallback(
    (index) => setSelectedIds((ids) => swap(ids, index, 1)),
    []
  );

  const handleDiscard = useCallback(() => setSelectedIds(savedIds), [savedIds]);

  const handleSave = useCallback(async () => {
    // Pin the branch for the whole round trip. The write must land on the
    // branch that was on screen when Save was pressed, and the reload after it
    // must not fire against a branch the manager has since moved away from.
    const targetBranchId = branchId;
    setSaving(true);
    try {
      await axios.put(
        FEATURED_URL + targetBranchId,
        { branch_course_ids: selectedIds },
        { headers: { "x-auth-token": authToken } }
      );
      setToastVariant("success");
      setToastMessage(
        selectedIds.length === 0
          ? "Featured courses cleared for this branch."
          : `Saved ${selectedIds.length} featured course${
              selectedIds.length === 1 ? "" : "s"
            }.`
      );
      setToastOpen(true);
      if (shownBranchRef.current === targetBranchId) {
        await fetchFeatured(targetBranchId);
      }
    } catch (e) {
      setToastVariant("error");
      setToastMessage(
        (e.response && e.response.data && e.response.data.message) ||
          "Could not save the featured courses."
      );
      setToastOpen(true);
    } finally {
      setSaving(false);
    }
  }, [branchId, authToken, selectedIds, fetchFeatured]);

  return (
    <>
      {/* Nothing is written until Save, so leaving with edits pending loses
          them silently. Same safeguard the trial scheduler uses. */}
      <Prompt
        when={isDirty}
        message="You have unsaved changes to this branch's featured courses. Leave without saving?"
      />

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={toastOpen}
        autoHideDuration={4000}
        onClose={() => setToastOpen(false)}
      >
        <SnackbarWrapper
          onClose={() => setToastOpen(false)}
          variant={toastVariant}
          message={toastMessage}
        />
      </Snackbar>

      <Card>
        <CardHeader title="Featured Courses">
          <CardHeaderToolbar>
            <button
              type="button"
              className="btn btn-light mr-2"
              onClick={handleDiscard}
              disabled={busy || !isDirty}
            >
              Discard changes
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSave}
              disabled={busy || !isDirty || !branchId}
            >
              {saving ? "Saving…" : "Save featured courses"}
            </button>
          </CardHeaderToolbar>
        </CardHeader>
        <CardBody>
          <p className="text-muted">
            Pick up to {maxFeatured} streams to highlight at the top of this
            branch's page on the public website, and put them in the order
            parents should see them. A stream with no live classes and no
            call-to-enroll is left off the website until the branch offers it
            again — its slot is kept in the meantime.
          </p>

          {branchId ? null : (
            <div className="text-muted">
              Select a branch to manage its featured courses.
            </div>
          )}
          {error ? <div className="text-danger">{error}</div> : null}
          {loading ? <LinearProgress color="secondary" /> : null}

          {branchId && !loading && !error ? (
            <>
              <h5 className="mt-5 mb-3 font-weight-bolder">
                Featured on the website{" "}
                <span className="text-muted font-size-sm font-weight-normal">
                  ({featured.length} of {maxFeatured})
                </span>
              </h5>
              <Paper className="mb-8">
                <div style={{ overflowX: "auto" }}>
                  <FeaturedCourseSlots
                    featured={featured}
                    maxFeatured={maxFeatured}
                    disabled={busy}
                    onMoveUp={handleMoveUp}
                    onMoveDown={handleMoveDown}
                    onRemove={handleRemove}
                  />
                </div>
              </Paper>

              <div className="d-flex align-items-center justify-content-between flex-wrap mb-3">
                <h5 className="mb-0 font-weight-bolder">
                  This branch's streams
                </h5>
                <input
                  type="search"
                  className="form-control w-auto"
                  style={{ minWidth: "260px" }}
                  placeholder="Search by name or code"
                  aria-label="Search this branch's streams"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              {/* role="status" announces the cap when it is first reached;
                  AT_CAP_MESSAGE_ID wires it to the Feature buttons it explains,
                  so the reason is reachable from the control itself and not
                  only from a line of text further up the page. */}
              {atCap ? (
                <div
                  id={AT_CAP_MESSAGE_ID}
                  role="status"
                  className="text-muted mb-3"
                >
                  All {maxFeatured} slots are taken — remove one above to
                  feature a different stream.
                </div>
              ) : null}
              <Paper>
                <div style={{ overflowX: "auto" }}>
                  <AvailableCoursesTable
                    courses={available}
                    atCap={atCap}
                    atCapMessageId={AT_CAP_MESSAGE_ID}
                    disabled={busy}
                    onAdd={handleAdd}
                  />
                </div>
              </Paper>
            </>
          ) : null}
        </CardBody>
      </Card>
    </>
  );
}
